import { ChevronDown, ChevronRight, Folder, GripVertical, Hash, Lock, Mic } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

export interface PermissionTreeChannel {
  id: string;
  name: string;
  type: string;
  permissionOverwrites?: Array<{ roleId: string; role: string; allow: string[]; deny: string[] }>;
}

export interface PermissionTreeCategory {
  id: string;
  name: string;
  channels: PermissionTreeChannel[];
  permissionOverwrites?: Array<{ roleId: string; role: string; allow: string[]; deny: string[] }>;
}

interface Props {
  categories: PermissionTreeCategory[];
  selectedChannelId: string | null;
  onSelectChannel: (channel: PermissionTreeChannel, categoryName: string) => void;
  onMoveChannel?: (channelId: string, fromCategoryId: string, toCategoryId: string) => void;
  movingChannelId?: string | null;
}

function channelIcon(type: string) {
  if (type === 'voice' || type === 'stage') return Mic;
  return Hash;
}

function isPrivateChannel(channel: PermissionTreeChannel): boolean {
  return (channel.permissionOverwrites?.length || 0) > 0;
}

export function PermissionTreeView({
  categories,
  selectedChannelId,
  onSelectChannel,
  onMoveChannel,
  movingChannelId,
}: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dragChannel, setDragChannel] = useState<{ channelId: string; fromCategoryId: string } | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const canDrag = Boolean(onMoveChannel);

  const toggleCategory = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDragStart = (
    event: React.DragEvent,
    channel: PermissionTreeChannel,
    categoryId: string
  ) => {
    if (!canDrag) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', channel.id);
    setDragChannel({ channelId: channel.id, fromCategoryId: categoryId });
  };

  const handleDragEnd = () => {
    setDragChannel(null);
    setDropTargetId(null);
  };

  const handleDragOver = (event: React.DragEvent, categoryId: string) => {
    if (!canDrag || !dragChannel) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropTargetId(categoryId);
  };

  const handleDrop = (event: React.DragEvent, categoryId: string) => {
    event.preventDefault();
    if (!dragChannel || !onMoveChannel) return;

    if (dragChannel.fromCategoryId !== categoryId) {
      onMoveChannel(dragChannel.channelId, dragChannel.fromCategoryId, categoryId);
    }

    setDragChannel(null);
    setDropTargetId(null);
  };

  return (
    <div className="rounded-md border divide-y max-h-[520px] overflow-auto">
      {canDrag && (
        <p className="px-3 py-2 text-xs text-muted-foreground border-b bg-muted/20">
          Trascina un canale su una categoria per spostarlo su Discord.
        </p>
      )}
      {categories.length === 0 && (
        <p className="p-4 text-sm text-muted-foreground">Nessuna categoria trovata.</p>
      )}
      {categories.map((category) => {
        const isOpen = expanded[category.id] !== false;
        const isDropTarget = dropTargetId === category.id && dragChannel?.fromCategoryId !== category.id;

        return (
          <div
            key={category.id}
            onDragOver={(event) => handleDragOver(event, category.id)}
            onDragLeave={() => setDropTargetId((prev) => (prev === category.id ? null : prev))}
            onDrop={(event) => handleDrop(event, category.id)}
            className={cn(isDropTarget && 'bg-primary/10 ring-1 ring-inset ring-primary/40')}
          >
            <button
              type="button"
              onClick={() => toggleCategory(category.id)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 text-sm font-medium',
                isDropTarget && 'bg-primary/5'
              )}
            >
              {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
              <Folder className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="truncate">{category.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">{category.channels.length}</span>
            </button>
            {isOpen && (
              <div className="pb-1">
                {category.channels.length === 0 && isDropTarget && (
                  <p className="pl-9 pr-3 py-2 text-xs text-primary">Rilascia qui</p>
                )}
                {category.channels.map((channel) => {
                  const Icon = channelIcon(channel.type);
                  const selected = selectedChannelId === channel.id;
                  const isDragging = dragChannel?.channelId === channel.id;
                  const isMoving = movingChannelId === channel.id;

                  return (
                    <div
                      key={channel.id}
                      draggable={canDrag && !isMoving}
                      onDragStart={(event) => handleDragStart(event, channel, category.id)}
                      onDragEnd={handleDragEnd}
                      className={cn(
                        'flex items-center gap-1 pl-6 pr-1',
                        isDragging && 'opacity-40',
                        isMoving && 'opacity-60'
                      )}
                    >
                      {canDrag && (
                        <GripVertical className="w-3 h-3 shrink-0 text-muted-foreground/50 cursor-grab active:cursor-grabbing" />
                      )}
                      <button
                        type="button"
                        onClick={() => onSelectChannel(channel, category.name)}
                        disabled={isMoving}
                        className={cn(
                          'flex-1 flex items-center gap-2 py-2 pr-2 text-left text-sm hover:bg-muted/40 rounded-sm',
                          selected && 'bg-primary/10 text-primary'
                        )}
                      >
                        <Icon className="w-4 h-4 shrink-0 opacity-70" />
                        <span className="truncate">{channel.name}</span>
                        {isMoving && <span className="text-xs text-muted-foreground ml-auto">…</span>}
                        {!isMoving && isPrivateChannel(channel) && (
                          <Lock className="w-3 h-3 ml-auto shrink-0 opacity-60" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
